import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Check, CalendarPlus, ListChecks, AlertTriangle, Play } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// MarkAcceptedDialog — lifecycle v2 acceptance with item review.
// =====================================================================
// The patient may accept all, reject all, or pick line items. The
// dialog defaults to "accepting all" (most common case) but lets the
// receptionist uncheck items the patient declined.
//
// Three resolutions:
//   1. ≥1 item kept checked + "Pick a date now"  → Plan goes to
//      Scheduled (parent opens the booking modal). accept_plan
//      flips the kept items to Accepted and the rest to Declined.
//   2. ≥1 item kept checked + "Add to booking queue" → Plan goes to
//      Needs Booking. Same item flips. Front desk follows up to book.
//   3. Patient declined the whole plan → accept_plan with empty list
//      flips every item to Declined; the plan auto-rolls to Rejected
//      via _recompute_plan_status.
//
// No money is collected at this moment regardless of path. Charges
// happen at the visit's checkout via complete_checkout's plan-aware
// path (PR 3d.2).
// =====================================================================

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  const pesos = Math.round(centavos / 100);
  return `₱${pesos.toLocaleString()}`;
}

function itemLine(it) {
  const proc = it.procedure_name || "Procedure";
  const tooth = it.tooth_number ? `#${it.tooth_number}` : "";
  const surface = it.surface ? ` ${it.surface}` : "";
  return [proc, tooth, surface].filter(Boolean).join(" ").trim();
}

function hasStartableVisitOne(it) {
  const visits = Array.isArray(it?.procedure_visits) ? it.procedure_visits : [];
  return visits.some((visit) =>
    Number(visit?.sequence || 1) === 1
      && !visit?.appointment_id
      && !visit?.completed_at
      && (visit?.outcome === null || visit?.outcome === undefined),
  );
}

export default function MarkAcceptedDialog({
  open,
  onOpenChange,
  plan,
  onAccepted,
  startTodayAppointmentId = null,
  initialAcceptedIds = null,
  schedulingOnly = false,
  patientId = null,
  patientName = null,
}) {
  const [submitting, setSubmitting]   = useState(false);
  const [pendingPath, setPendingPath] = useState(null);   // 'schedule' | 'queue' | 'reject'
  const [acceptedSet, setAcceptedSet] = useState(() => new Set());
  const [rejectAll, setRejectAll]     = useState(false);
  // Set to the pending accept path when the patient already has an active
  // plan and we need a "accept a second?" confirmation before proceeding.
  const [secondPlanConfirm, setSecondPlanConfirm] = useState(null);
  // "Going with this plan?" — the patient's OTHER open proposals. Accepting
  // one usually means the others were alternatives; we offer to close them as
  // Superseded (ticked by default; staff can untick to keep genuinely separate
  // work). supersedeConfirm holds the pending accept path while shown.
  const [otherPlans, setOtherPlans]         = useState([]);
  const [supersedeSet, setSupersedeSet]     = useState(() => new Set());
  const [supersedeConfirm, setSupersedeConfirm] = useState(null);

  // Reviewable items: anything currently in Candidate/Proposed status.
  // Items already in Scheduled / Completed / Cancelled / Declined are
  // out-of-scope for this dialog — they've already been acted on.
  const reviewable = useMemo(() => {
    if (!plan?.items) return [];
    return plan.items.filter((it) =>
      it.status === "Candidate" || it.status === "Proposed",
    );
  }, [plan]);

  // Default selection: everything checked. Resets each time the
  // dialog opens or the plan changes.
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(initialAcceptedIds)) {
      setAcceptedSet(new Set(initialAcceptedIds));
      setRejectAll(initialAcceptedIds.length === 0);
    } else {
      setAcceptedSet(new Set(reviewable.map((it) => it.id)));
      setRejectAll(false);
    }
    setSubmitting(false);
    setPendingPath(null);
    setSecondPlanConfirm(null);
    setSupersedeConfirm(null);
  }, [open, reviewable, initialAcceptedIds]);

  // Load the patient's OTHER open proposals (for the "close the others?"
  // confirm). Skipped for scheduling-only opens (no acceptance happening).
  useEffect(() => {
    const pid = patientId ?? plan?.patient_id ?? null;
    if (!open || schedulingOnly || !pid || !plan?.id) { setOtherPlans([]); return; }
    let alive = true;
    supabase.rpc("other_proposed_plans", { p_patient_id: pid, p_exclude_plan_id: plan.id })
      .then(({ data, error }) => {
        if (!alive) return;
        const rows = (!error && Array.isArray(data)) ? data : [];
        setOtherPlans(rows);
        setSupersedeSet(new Set(rows.map((r) => r.plan_id)));   // ticked by default
      });
    return () => { alive = false; };
  }, [open, schedulingOnly, patientId, plan?.id, plan?.patient_id]);

  const acceptedItems = useMemo(
    () => reviewable.filter((it) => acceptedSet.has(it.id)),
    [reviewable, acceptedSet],
  );

  const acceptedTotal = useMemo(
    () => acceptedItems.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0),
    [acceptedItems],
  );

  const planTotal = useMemo(
    () => reviewable.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0),
    [reviewable],
  );

  const visitOneCount = useMemo(
    () => acceptedItems.filter((it) => Number(it.phase_number || 1) === 1).length,
    [acceptedItems],
  );

  const startTodayEligibleCount = useMemo(
    () => acceptedItems.filter(hasStartableVisitOne).length,
    [acceptedItems],
  );
  const showStartToday = !!startTodayAppointmentId;
  const canStartToday = showStartToday
    && acceptedItems.length > 0
    && startTodayEligibleCount === acceptedItems.length;

  if (!open) return null;

  function toggleItem(id) {
    setAcceptedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleRejectAll() {
    setRejectAll((v) => {
      const nv = !v;
      if (nv) {
        // Switching ON → uncheck everything visually.
        setAcceptedSet(new Set());
      } else {
        // Switching OFF → restore default (everything checked).
        setAcceptedSet(new Set(reviewable.map((it) => it.id)));
      }
      return nv;
    });
  }

  async function submit(path, opts = {}) {
    if (submitting) return;
    setSubmitting(true);
    setPendingPath(path);

    // Second active-plan guard. Accepting is allowed up to 2 active plans
    // per patient (backend hard-caps a 3rd). When one active plan already
    // exists, warn before accepting a second. 'reject' never triggers it.
    const pid = patientId ?? plan?.patient_id ?? null;
    if (path !== "reject" && !opts.skipGuard && pid) {
      try {
        const { data: slot } = await supabase.rpc("tx_plan_slot_state", { p_patient_id: pid });
        if (Number(slot?.active_count || 0) >= 1) {
          setSubmitting(false);
          setPendingPath(null);
          setSecondPlanConfirm(path);
          return;
        }
      } catch {
        // Non-blocking: the database still enforces the cap. Proceed.
      }
    }

    // "Going with this plan?" guard. When the patient has other open quotes
    // and we're actually accepting (not rejecting, not the start-today path),
    // offer to close them. Not shown for start_today (that path keeps its own
    // RPC). skipSupersede is set once the confirm has been answered.
    if (path !== "reject" && path !== "start_today" && !opts.skipSupersede && otherPlans.length > 0) {
      setSubmitting(false);
      setPendingPath(null);
      setSupersedeConfirm(path);
      return;
    }

    // Build the accepted_item_ids payload.
    //   path='reject' → empty array (decline all → plan goes to Rejected)
    //   acceptedSet has all reviewable items → NULL (legacy fast path)
    //   otherwise → explicit array of accepted ids
    let acceptedIds;
    if (path === "reject") {
      acceptedIds = [];
    } else if (acceptedSet.size === reviewable.length) {
      acceptedIds = null;
    } else {
      acceptedIds = Array.from(acceptedSet);
    }

    // Chosen "close the others" ids — only for the accept_plan_over_others path.
    const supersedeIds = Array.isArray(opts.supersedeIds) ? opts.supersedeIds : [];

    let rpcName;
    let rpcPayload;
    if (path === "start_today") {
      rpcName = "accept_plan_and_start_today";
      rpcPayload = {
        p_plan_id: plan.id,
        p_appointment_id: startTodayAppointmentId,
        p_accepted_item_ids: acceptedIds,
      };
    } else if (path === "reject") {
      // Rejecting a plan never closes the patient's other quotes.
      rpcName = "accept_plan";
      rpcPayload = { p_plan_id: plan.id, p_accepted_item_ids: acceptedIds };
    } else {
      // accept_plan_over_others accepts the winner and closes the chosen
      // others as Superseded, atomically. Empty supersede list == plain accept.
      rpcName = "accept_plan_over_others";
      rpcPayload = {
        p_plan_id:            plan.id,
        p_accepted_item_ids:  acceptedIds,
        p_supersede_plan_ids: supersedeIds.length > 0 ? supersedeIds : null,
      };
    }

    const { data, error } = await supabase.rpc(rpcName, rpcPayload);

    if (error) {
      toast.error(error.message ?? "Could not save the patient's decision.");
      setSubmitting(false);
      setPendingPath(null);
      return;
    }

    if (data?.noop) {
      toast.message("Plan was already reviewed.");
    } else if (path === "reject") {
      toast.success("Plan marked as rejected.");
    } else if (path === "start_today") {
      toast.success(`Recorded — ${data?.accepted_count ?? 0} item${data?.accepted_count === 1 ? "" : "s"} accepted. Visit 1 started today.`);
    } else if (path === "schedule") {
      toast.success(`Recorded — ${data?.accepted_count ?? 0} item${data?.accepted_count === 1 ? "" : "s"} accepted. Pick a date next.`);
    } else {
      toast.success(`Recorded — ${data?.accepted_count ?? 0} item${data?.accepted_count === 1 ? "" : "s"} accepted. Added to the booking queue.`);
    }

    const closed = Number(data?.superseded_count || 0);
    if (closed > 0) {
      toast.message(`${closed} other plan${closed === 1 ? "" : "s"} closed.`);
    }

    onAccepted?.({ ...data, path });
    onOpenChange?.(false);
    setSubmitting(false);
    setPendingPath(null);
  }

  function toggleSupersede(id) {
    setSupersedeSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // "Going with this plan?" — offer to close the patient's other open quotes.
  if (supersedeConfirm) {
    const n = otherPlans.length;
    const who = patientName || "This patient";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" data-testid="supersede-confirm">
          <h2 className="text-base font-semibold text-slate-900">Going with this plan?</h2>
          <p className="text-sm text-slate-500 mt-1.5">
            {who} has {n} other open plan{n === 1 ? "" : "s"}. We'll close {n === 1 ? "it" : "them"} so
            {n === 1 ? " it doesn't" : " they don't"} stay pending — no need to follow up later.
            Still needs {n === 1 ? "it" : "one"}? Just untick to keep.
          </p>
          <div className="mt-3 space-y-1.5">
            {otherPlans.map((op) => (
              <label key={op.plan_id}
                className="flex items-start gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                data-testid={`supersede-row-${op.plan_id}`}>
                <input type="checkbox" className="mt-0.5"
                  checked={supersedeSet.has(op.plan_id)}
                  onChange={() => toggleSupersede(op.plan_id)} />
                <span className="text-sm text-slate-700 min-w-0">
                  <span className="font-semibold">Close · {formatPHP(Number(op.total_fee_centavos) || 0)}</span>
                  {op.summary ? <span className="text-slate-500"> · {op.summary}</span> : null}
                </span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button"
              data-testid="supersede-keep-all"
              onClick={() => {
                const path = supersedeConfirm;
                setSupersedeConfirm(null);
                submit(path, { skipGuard: true, skipSupersede: true, supersedeIds: [] });
              }}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 hover:border-slate-400">
              Keep all
            </button>
            <button type="button"
              data-testid="supersede-accept-close"
              onClick={() => {
                const path = supersedeConfirm;
                setSupersedeConfirm(null);
                submit(path, { skipGuard: true, skipSupersede: true, supersedeIds: Array.from(supersedeSet) });
              }}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700">
              Accept &amp; close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Second active-plan confirmation. Shown when the patient already has an
  // active plan and the user is about to accept another (up to 2 allowed).
  if (secondPlanConfirm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" data-testid="second-plan-confirm">
          <div className="flex items-center gap-2 text-slate-700 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h2 className="text-base font-semibold">This patient already has an active plan</h2>
          </div>
          <p className="text-sm text-slate-500">
            Accepting this makes a second active treatment plan for the patient. Each plan keeps its own
            schedule and billing. A patient can have at most two active plans at once.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSecondPlanConfirm(null)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 hover:border-slate-400"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="second-plan-confirm-accept"
              onClick={() => {
                const path = secondPlanConfirm;
                setSecondPlanConfirm(null);
                submit(path, { skipGuard: true });
              }}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-700"
            >
              Accept second plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No reviewable items? The plan is in some other state — guard
  // gracefully (real callers should check before opening).
  if (reviewable.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
          <div className="flex items-center gap-2 text-slate-700 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h2 className="text-base font-semibold">Nothing to review</h2>
          </div>
          <p className="text-sm text-slate-500">
            Every item on this plan has already been acted on. Use Revise plan if you need to make changes.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 hover:border-slate-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mark-accepted-title"
      data-testid="mark-accepted-dialog"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h2 id="mark-accepted-title" className="text-base font-semibold text-slate-900">
              Review the patient's decision
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Plan total ₱{Math.round(planTotal / 100).toLocaleString()} · {reviewable.length} item{reviewable.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onOpenChange?.(false)}
            disabled={submitting}
            data-testid="mark-accepted-close"
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Reject-all off-ramp — hidden when selection came from the inline checklist */}
          {!schedulingOnly ? (
            <label className="flex items-start gap-2 px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 cursor-pointer hover:bg-rose-100">
              <input
                type="checkbox"
                checked={rejectAll}
                onChange={toggleRejectAll}
                data-testid="mark-accepted-reject-all"
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-rose-900">Patient declined the whole plan</span>
                <span className="block text-[11px] text-rose-700/80 mt-0.5">
                  Toggle on to decline every item. Plan will move to Rejected.
                </span>
              </span>
            </label>
          ) : null}

          {!rejectAll && !schedulingOnly ? (
            <>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Which items did the patient accept?
                </p>
                <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                  {reviewable.map((it) => {
                    const checked = acceptedSet.has(it.id);
                    return (
                      <li key={it.id} className={checked ? "" : "bg-slate-50/60"}>
                        <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleItem(it.id)}
                            data-testid={`mark-accepted-item-${it.id}`}
                          />
                          <span className={`flex-1 text-sm ${checked ? "text-slate-900" : "text-slate-400 line-through"}`}>
                            {itemLine(it)}
                          </span>
                          <span className={`text-xs tabular-nums ${checked ? "text-slate-700" : "text-slate-400"}`}>
                            {formatPHP(Number(it.fee_centavos) || 0)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total accepting</p>
                  <p className="text-lg font-semibold text-slate-900 tabular-nums" data-testid="mark-accepted-total">
                    {formatPHP(acceptedTotal)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Visit 1</p>
                  <p className="text-sm font-medium text-slate-700">
                    {visitOneCount} item{visitOneCount === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-100 bg-white px-5 py-4 shrink-0">
          {rejectAll ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => submit("reject")}
              data-testid="mark-accepted-reject-confirm"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-50"
            >
              {pendingPath === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Mark plan as Rejected
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-700">When does Visit 1 happen?</p>
              <div className={`grid grid-cols-1 ${showStartToday ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-2`}>
                {showStartToday ? (
                  <button
                    type="button"
                    disabled={submitting || !canStartToday}
                    onClick={() => submit("start_today")}
                    data-testid="mark-accepted-start-today"
                    className="flex items-start gap-3 px-4 py-3 rounded-xl border border-teal-200 bg-teal-50 hover:bg-teal-100 text-left disabled:opacity-50"
                  >
                    <span className="shrink-0 w-9 h-9 rounded-lg bg-teal-600 text-white flex items-center justify-center">
                      {pendingPath === "start_today" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-teal-900">Start Today</span>
                      <span className="block text-[11px] text-teal-800/80 mt-0.5">
                        Use this visit as Visit 1.
                      </span>
                    </span>
                  </button>
                ) : null}

                <button
                  type="button"
                  disabled={submitting || acceptedSet.size === 0}
                  onClick={() => submit("schedule")}
                  data-testid="mark-accepted-schedule"
                  className="flex items-start gap-3 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-left disabled:opacity-50"
                >
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                    {pendingPath === "schedule" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-emerald-900">Pick a date now</span>
                    <span className="block text-[11px] text-emerald-800/80 mt-0.5">
                      Open the scheduler next.
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  disabled={submitting || acceptedSet.size === 0}
                  onClick={() => submit("queue")}
                  data-testid="mark-accepted-queue"
                  className="flex items-start gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-slate-400 text-left disabled:opacity-50"
                >
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-slate-700 text-white flex items-center justify-center">
                    {pendingPath === "queue" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">No date yet — add to booking queue</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">
                      Front desk follows up to book.
                    </span>
                  </span>
                </button>
              </div>
              {acceptedSet.size === 0 ? (
                <p className="text-[11px] text-amber-700 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" />
                  Check at least one item, or toggle "Patient declined the whole plan" above.
                </p>
              ) : null}
            </div>
          )}

          <p className="text-[11px] text-slate-500 mt-3 leading-tight flex items-start gap-2">
            <Check className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
            <span>
              No charge or payment writes here. The plan total is locked at the first visit's checkout, when the patient is at the chair.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
